import { useMemo, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useFunction } from '@/hooks/useFunction';
import { formatRelativeTime } from '@/utils/date';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, Toolbar } from '@/components/inputs';
import DataTable, { RowLink } from '@/components/admin/DataTable';
import CurrencyDialog, { convert } from '@/components/admin/CurrencyDialog';
import { BOOL_STYLES } from '@/shared/constants';
import { CURRENCY_FIELDS } from '@/shared/fields';
import { rows } from '@/shared/data';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/currencies', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'Coins', label: 'Currencies', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 48 };

const SHOWN = ['Shown', 'Hidden'];

export default function AdminCurrencies() {
  const [shown, setShown] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('currencies_c').select([...CURRENCY_FIELDS, 'ModifiedOn']);
    if (shown !== 'all') query = query.where(eq('active_c', shown === 'Shown'));
    return query.orderBy('Name').limit(100).fetch().then(rows);
  }, [shown]);

  const list = data ?? [];
  const newest = useMemo(
    () => list.reduce((acc, c) => (c.ModifiedOn && (!acc || c.ModifiedOn > acc) ? c.ModifiedOn : acc), null),
    [list],
  );

  const refresh = useFunction(import.meta.env.VITE_REFRESH_RATES, { successMessage: 'Rates refreshed' });

  const refreshNow = async () => {
    try {
      await refresh.invoke({ force: true });
    } catch {
      // useFunction already shows the error.
    } finally {
      run();
    }
  };

  const columns = [
    {
      label: 'Code',
      render: (c) => (
        <RowLink to={`/admin/currencies/${c.Id}`} mono sub={c.symbol_c}>
          {c.Name}
        </RowLink>
      ),
    },
    {
      label: 'Rate',
      className: 'tabular-nums',
      render: (c) => `1 USD = ${Number(c.rate_c || 0).toFixed(4)} ${c.Name}`,
    },
    {
      label: 'A $28 plan',
      className: 'hidden md:table-cell tabular-nums text-muted-foreground',
      render: (c) => convert(28, c),
    },
    { label: 'Checkout', render: (c) => <StatusBadge value={c.active_c ? 'Yes' : 'No'} styles={BOOL_STYLES} /> },
  ];

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow="Store"
        title="Currencies"
        description={
          newest
            ? `Rates for the price estimates at checkout. They update daily from open.er-api.com. Last updated ${formatRelativeTime(newest)}.`
            : 'Rates for the price estimates at checkout. They update daily from open.er-api.com.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={refreshNow} disabled={refresh.loading}>
              <ApperIcon name={refresh.loading ? 'Loader2' : 'RefreshCw'} size={16} className={refresh.loading ? 'animate-spin' : undefined} />
              Refresh rates now
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <ApperIcon name="Plus" size={16} />
              Add currency
            </Button>
          </div>
        }
      />

      <Toolbar>
        <FilterSelect value={shown} onChange={setShown} values={SHOWN} labels={{ Shown: 'Shown at checkout', Hidden: 'Hidden at checkout' }} allLabel="All currencies" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        rows={4}
        isEmpty={!list.length}
        empty={{
          icon: 'Coins',
          title: shown !== 'all' ? 'No currencies match' : 'No currencies yet',
          description: shown !== 'all' ? 'Nothing fits this filter.' : 'Add USD at a rate of 1 first.',
          action: shown !== 'all' ? () => setShown('all') : () => setCreateOpen(true),
          actionLabel: shown !== 'all' ? 'Clear filter' : 'Add currency',
        }}
      >
        <DataTable columns={columns} rows={list} />
      </ListBody>

      <CurrencyDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
